import {
  TemplateChannel,
  TemplateContext,
  type DashboardTableSortState,
  type TemplateLibraryEntry,
  type TemplateVariableDefinition,
} from '@fluenceur/types';

/**
 * Columns the template library table can be sorted by. The list query returns
 * an unpaginated array, so sorting and filtering both happen client-side.
 */
export type TemplateLibrarySortBy = 'name' | 'channel' | 'context' | 'language';

export const TEMPLATE_LIBRARY_SORT_OPTIONS = [
  'channel',
  'name',
  'context',
  'language',
] as const satisfies readonly [TemplateLibrarySortBy, ...TemplateLibrarySortBy[]];

/**
 * Default ordering: by channel, then name. The query state hook only carries a
 * single (column, direction) pair, so `channel` is the primary key and `name`
 * is applied as a stable tiebreaker inside the comparator below.
 */
export const TEMPLATE_LIBRARY_DEFAULT_SORT = {
  sortBy: 'channel',
  sortDirection: 'asc',
} as const satisfies DashboardTableSortState<TemplateLibrarySortBy>;

/** i18n key for a channel label. */
export const channelLabelKey = (channel: TemplateChannel): string =>
  `templates.channels.${channel}`;

/** i18n key for a context label. */
export const contextLabelKey = (context: TemplateContext): string =>
  `templates.contexts.${context}`;

/** i18n key for a language label. */
export const languageLabelKey = (language: string): string => `templates.languages.${language}`;

/** Stable ordering used as the secondary sort key for channels. */
const CHANNEL_ORDER: Record<TemplateChannel, number> = {
  [TemplateChannel.EMAIL]: 0,
  [TemplateChannel.INSTAGRAM_DM]: 1,
  [TemplateChannel.INSTAGRAM_COMMENT]: 2,
};

const CONTEXT_ORDER: Record<TemplateContext, number> = {
  [TemplateContext.INVITATION]: 0,
  [TemplateContext.GENERIC]: 1,
  [TemplateContext.COLLAB]: 2,
};

const compareStrings = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: 'base' });

/**
 * Compare two entries for a given primary sort column. `name` is always used as
 * the final tiebreaker so the order is deterministic regardless of column.
 */
const compareBy = (
  a: TemplateLibraryEntry,
  b: TemplateLibraryEntry,
  sortBy: TemplateLibrarySortBy
): number => {
  switch (sortBy) {
    case 'channel': {
      const byChannel = CHANNEL_ORDER[a.channel] - CHANNEL_ORDER[b.channel];
      return byChannel !== 0 ? byChannel : compareStrings(a.name, b.name);
    }
    case 'context': {
      const byContext = CONTEXT_ORDER[a.context] - CONTEXT_ORDER[b.context];
      return byContext !== 0 ? byContext : compareStrings(a.name, b.name);
    }
    case 'language': {
      const byLanguage = compareStrings(a.language, b.language);
      return byLanguage !== 0 ? byLanguage : compareStrings(a.name, b.name);
    }
    case 'name':
    default:
      return compareStrings(a.name, b.name);
  }
};

export interface TemplateLibraryFilters {
  channel?: TemplateChannel;
  context?: TemplateContext;
}

/**
 * Apply client-side channel/context filtering then sort. Returns a new array;
 * the input is never mutated.
 */
export const filterAndSortTemplates = (
  templates: readonly TemplateLibraryEntry[],
  filters: TemplateLibraryFilters,
  sort: DashboardTableSortState<TemplateLibrarySortBy>
): TemplateLibraryEntry[] => {
  const filtered = templates.filter((template) => {
    if (filters.channel && template.channel !== filters.channel) {
      return false;
    }
    if (filters.context && template.context !== filters.context) {
      return false;
    }
    return true;
  });

  const direction = sort.sortDirection === 'asc' ? 1 : -1;
  return [...filtered].sort((a, b) => compareBy(a, b, sort.sortBy) * direction);
};

/**
 * A Fluenceur template (the pair's system default or any other system row) is
 * read-only: it cannot be edited, deleted, or promoted to the brand default. It
 * can only be duplicated.
 */
export const isReadOnlyTemplate = (template: TemplateLibraryEntry): boolean =>
  template.isSystem || template.isSystemDefault;

/**
 * A Fluenceur default is stored without a name and reaches the dashboard with a
 * generated `channel:context:language` label. Returns the i18n key naming it
 * after its context, or `null` when the stored name should be shown as is.
 */
export const templateNameLabelKey = (template: TemplateLibraryEntry): string | null =>
  isReadOnlyTemplate(template) &&
  template.name === `${template.channel}:${template.context}:${template.language}`
    ? contextLabelKey(template.context)
    : null;

/**
 * Build a sample value map for previewing a template body. Variables are
 * substituted with their registry fallback (or the variable label when no
 * fallback is defined) so brands see a realistic render without a server round
 * trip. Required variables without a fallback keep a bracketed placeholder.
 */
export const buildSampleValues = (
  variables: readonly TemplateVariableDefinition[]
): Record<string, string> => {
  const values: Record<string, string> = {};
  for (const variable of variables) {
    if (variable.fallback && variable.fallback.trim() !== '') {
      values[variable.key] = variable.fallback;
    } else {
      values[variable.key] = `[${variable.label}]`;
    }
  }
  return values;
};

const VARIABLE_TOKEN = /\{\{\s*([\w.]+)\s*\}\}/g;

/**
 * Render a preview by replacing `{{key}}` tokens with sample values. Unknown
 * tokens are left untouched so authors notice typos. Pure and synchronous: no
 * render engine is reimplemented, this is a display-only substitution that
 * mirrors the variable set of the selected (channel, context) pair.
 */
export const renderTemplatePreview = (
  content: string,
  sampleValues: Record<string, string>
): string =>
  content.replace(VARIABLE_TOKEN, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(sampleValues, key) ? sampleValues[key] : match
  );
